import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applyAuthSession } = useAuth();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!token) navigate('/member-login', { replace: true });
  }, [token, navigate]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/activate`, { token, password });
      // Single canonical session writer — stores token under TOKEN_KEY,
      // schedules silent refresh timers, and populates user state. Without
      // this, subsequent authed calls (e.g. /auth/onboarding/complete) fire
      // without a Bearer header and fail silently.
      applyAuthSession(res.data);
      setActivated(true);
      toast.success('Account activated! Welcome to the community.');
      // First-time user → onboarding video, then Community Home.
      setTimeout(() => navigate('/welcome', { replace: true }), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally { setLoading(false); }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#002249] via-[#1D3557] to-[#002249] flex items-center justify-center px-4" data-testid="activate-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#002249] to-[#1D3557] px-8 py-8 text-center">
            <ShieldCheck className="w-12 h-12 text-white mx-auto mb-3" />
            <h1 className="text-xl font-bold text-white">Activate Your Account</h1>
            <p className="text-white/60 text-sm mt-1">Dominican Chamber of Commerce of Florida</p>
          </div>

          {activated ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#002249] mb-2">Account Activated!</h2>
              <p className="text-slate-600 text-sm">Taking you to a quick welcome...</p>
            </div>
          ) : (
            <form onSubmit={handleActivate} className="p-8 space-y-5">
              <p className="text-sm text-slate-600 text-center">
                Create your password to access the DCCF Member Portal.
              </p>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password (min 6 characters)"
                    className="pl-10 pr-10 h-11 rounded-lg"
                    required
                    data-testid="activate-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="pl-10 h-11 rounded-lg"
                    required
                    data-testid="activate-confirm-password"
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] rounded-lg font-semibold" data-testid="activate-submit">
                {loading ? 'Activating...' : 'Set Password & Activate'}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ActivatePage;
